import type {
  ProjectContainerOverviewFolder,
  ProjectContainerOverviewIntake,
  ProjectContainerSectionModel
} from '../tabs/home/types/ProjectContainerHomeTypes';

type LooseRecord = Record<string, unknown>;

/** Normalize one intake from SignalR (either PascalCase or camelCase). */
export const normalizeHomeFeedIntake = (raw: unknown): ProjectContainerOverviewIntake => {
  const o = raw as LooseRecord;
  const id = o.Id ?? o.id;
  const hasNew = o.HasNewMessage ?? o.hasNewMessage;
  return {
    Id: typeof id === 'number' ? id : Number(id),
    Subject: (o.Subject ?? o.subject ?? null) as string | null | undefined,
    HasNewMessage: hasNew === true
  };
};

/** Safe read for UI (REST JSON is often camelCase only). */
export const projectContainerIntakeIsUnread = (intake: unknown): boolean => {
  if (intake == null || typeof intake !== 'object') return false;
  const o = intake as LooseRecord;
  return o.HasNewMessage === true || o.hasNewMessage === true;
};

type FolderWithCamelIntakes = ProjectContainerOverviewFolder & { intakes?: unknown[] };

/**
 * Ensures each folder intake has PascalCase `HasNewMessage` so UI reads match ASP.NET JSON
 * (`hasNewMessage` camelCase) and SignalR payloads.
 */
export const normalizeProjectContainerSectionModel = (
  section: ProjectContainerSectionModel
): ProjectContainerSectionModel => {
  const loose = section as ProjectContainerSectionModel & { folders?: ProjectContainerOverviewFolder[] };
  const folders = section.Folders ?? loose.folders;
  if (folders == null || folders.length === 0) {
    return section;
  }

  const normalizedFolders: ProjectContainerOverviewFolder[] = folders.map((folder) => {
    const intakes =
      folder.Intakes ?? (folder as FolderWithCamelIntakes).intakes;
    if (intakes == null || !Array.isArray(intakes)) {
      return folder;
    }
    return {
      ...folder,
      Intakes: intakes.map((i) => normalizeHomeFeedIntake(i))
    };
  });

  return {
    ...section,
    Folders: normalizedFolders
  };
};
